import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import {
  CallInbound16Regular,
  CallOutbound16Regular,
  CheckmarkCircle16Filled,
  Circle16Regular,
  DismissCircle16Regular,
  Important16Filled,
} from '@fluentui/react-icons';
import type { MultiTableItem, MultiTableViewDefinition, TableRecord } from '@powerportalspro/core';
import { useT } from '@powerportalspro/react';
import { Calendar, type CalendarAppointment } from '@powerportalspro/react-calendar';
import { useMemo, type ReactNode } from 'react';
import { ACCOUNT_ID } from './DetailPageRecords';
import { DemoShowcaseLayout } from './DemoShowcaseLayout';

// The featured account's appointments, tasks and phone calls, a month at a time. Tasks and phone
// calls are drawn by templates of their own — whether a task is done and how urgent it is, which way
// a call went — and appointments as the calendar draws them. Each table's tooltip says what matters
// about its rows. The view names the columns the templates show, so the calendar reads them.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.CalendarShowcase.CalendarCustomRenderingMainDemo';

const COMPLETED = 1;
const CANCELED = 2;
const HIGH_PRIORITY = 2;
const ACTIVITIES_VIEW_ID = '7c1e5a9d-3f2b-4d6e-8a0c-9b4f2e6d1a02';
const ACCOUNT = { tableName: 'account', id: ACCOUNT_ID };

const useStyles = makeStyles({
  // A task or a phone call on the calendar: one line, its icons before its subject.
  block: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXXS,
    minWidth: 0,
    '& svg': {
      flexShrink: 0,
      width: '14px',
      height: '14px',
    },
  },
  subject: {
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  done: {
    textDecorationLine: 'line-through',
  },
  canceled: {
    opacity: 0.7,
  },
  urgent: {
    display: 'inline-flex',
    color: tokens.colorPaletteRedForeground1,
  },
  tooltip: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
  },
  list: {
    margin: 0,
    paddingInlineStart: tokens.spacingHorizontalXL,
  },
});

// The raw value behind a column, and its text as the server formatted it.
function read(record: TableRecord | null | undefined, column: string): unknown {
  const cell = record?.properties?.[column];
  return cell && typeof cell === 'object' && 'value' in cell ? cell.value : undefined;
}

function formatted(record: TableRecord | null | undefined, column: string): string {
  return record?.formattedValues?.[column] ?? '';
}

// The strings are HTML, written once for both stacks.
function Item({ text }: { text: string }) {
  return <li dangerouslySetInnerHTML={{ __html: text }} />;
}

export function CalendarCustomRenderingMainSample() {
  const styles = useStyles();
  const t = useT();

  // The account's appointments, tasks and phone calls, each table with the columns its template and
  // tooltip show: the calendar reads them, beside its own, as it reads the view.
  const views = useMemo<MultiTableViewDefinition[]>(
    () => [
      {
        id: ACTIVITIES_VIEW_ID,
        displayName: t(`${STRINGS_BASE}.view-activities`),
        tables: [
          {
            table: 'appointment',
            fetchXml: '<fetch><entity name="appointment"><attribute name="requiredattendees" /></entity></fetch>',
          },
          {
            table: 'task',
            fetchXml: '<fetch><entity name="task"><attribute name="prioritycode" /></entity></fetch>',
          },
          {
            table: 'phonecall',
            fetchXml: '<fetch><entity name="phonecall"><attribute name="directioncode" /><attribute name="phonenumber" /></entity></fetch>',
          },
        ],
      },
    ],
    [t],
  );

  const blockClass = (item: MultiTableItem) => mergeClasses(styles.block, read(item.record, 'statecode') === CANCELED && styles.canceled);

  // What each table's tooltip says, beside the subject.
  const tooltip = (appointment: CalendarAppointment): ReactNode => {
    const record = appointment.record;
    const outgoing = read(record, 'directioncode') === true;
    const number = read(record, 'phonenumber');
    const location = read(record, 'location');
    return (
      <div className={styles.tooltip}>
        <strong>{appointment.subject}</strong>
        {record &&
          (appointment.table === 'task' ? (
            <>
              <span>{t(`${STRINGS_BASE}.due`, [formatted(record, 'scheduledend')])}</span>
              <span>{t(`${STRINGS_BASE}.state-and-priority`, [formatted(record, 'statecode'), formatted(record, 'prioritycode')])}</span>
            </>
          ) : appointment.table === 'phonecall' ? (
            <>
              <span>{t(`${STRINGS_BASE}.${outgoing ? 'call-to' : 'call-from'}`, [typeof number === 'string' ? number : ''])}</span>
              <span>{formatted(record, 'statecode')}</span>
            </>
          ) : (
            <>
              <span>{t(`${STRINGS_BASE}.when`, [formatted(record, 'scheduledstart'), formatted(record, 'scheduledend')])}</span>
              {typeof location === 'string' && location && <span>{location}</span>}
              {formatted(record, 'requiredattendees') && (
                <span>{t(`${STRINGS_BASE}.attendees`, [formatted(record, 'requiredattendees')])}</span>
              )}
            </>
          ))}
      </div>
    );
  };

  return (
    <DemoShowcaseLayout
      title={t(`${STRINGS_BASE}.try-title`)}
      tips={
        <>
          <ul className={styles.list}>
            <Item text={t(`${STRINGS_BASE}.try-tasks`)} />
            <Item text={t(`${STRINGS_BASE}.try-calls`)} />
            <Item text={t(`${STRINGS_BASE}.try-tooltips`)} />
            <Item text={t(`${STRINGS_BASE}.try-week`)} />
          </ul>
        </>
      }
    >
      <Calendar
        defaultLayout="month"
        height="760px"
        regarding={ACCOUNT}
        customViewDefinitions={views}
        defaultViewId={ACTIVITIES_VIEW_ID}
        appointmentTooltip={tooltip}
        templates={{
          // A task: an icon for whether it is done, another when it is urgent, then its subject.
          task: (item) => {
            const state = read(item.record, 'statecode');
            return (
              <span className={blockClass(item)}>
                {state === COMPLETED ? <CheckmarkCircle16Filled /> : state === CANCELED ? <DismissCircle16Regular /> : <Circle16Regular />}
                {read(item.record, 'prioritycode') === HIGH_PRIORITY && (
                  <span className={styles.urgent}>
                    <Important16Filled />
                  </span>
                )}
                <span className={mergeClasses(styles.subject, state === COMPLETED && styles.done)}>{item.title}</span>
              </span>
            );
          },
          // A phone call: which way it went, then its subject.
          phonecall: (item) => (
            <span className={blockClass(item)}>
              {read(item.record, 'directioncode') === true ? <CallOutbound16Regular /> : <CallInbound16Regular />}
              <span className={styles.subject}>{item.title}</span>
            </span>
          ),
        }}
      />
    </DemoShowcaseLayout>
  );
}
